import { ApprovalCardStatusProvider } from '@/features/assistant/_components/approval-card-status-context';
import {
  cardReplyDisplay,
  hasVideoFootageRecoveryCard,
} from '@/features/assistant/_components/card-reply-display';
import { ComposerGateScope } from '@/features/assistant/_components/composer-gate-context';
import { CampaignPackageAdStatusRefresh } from '@/features/assistant/_components/rich/campaign-package-review';
import { SourceVideoPreview } from '@/features/assistant/_components/source-video-preview';
import { sourceVideoIdFromParts } from '@/features/assistant/lib/gallery-source-message';
import {
  getPackagePreparationNotes,
  getPackageSupersededAdPreviewToolCallIds,
} from '@/features/assistant/lib/package-ad-preview-supersession';
import {
  getContinuedCampaignErrorToolCallIds,
  getRecoveredCampaignErrorToolCallIds,
} from '@/features/assistant/lib/recovered-campaign-errors';
import { useStaleProposalReply } from '@/features/assistant/lib/stale-proposal-reply';
import type { VideoFootageSelection } from '@borradh-workspace/contracts/claire';
import type { ConfirmationSelection } from '@borradh-workspace/contracts/claire';
import type { UIMessage } from 'ai';
import { Bot } from 'lucide-react';
import { useMemo } from 'react';

import {
  Attachment,
  AttachmentPreview,
  Attachments,
} from '@/components/ai-elements/attachments';
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from '@/components/ai-elements/conversation';
import {
  Message,
  MessageContent,
  MessageResponse,
} from '@/components/ai-elements/message';
import {
  Reasoning,
  ReasoningContent,
  ReasoningTrigger,
} from '@/components/ai-elements/reasoning';
import {
  Source,
  Sources,
  SourcesContent,
  SourcesTrigger,
} from '@/components/ai-elements/sources';
import {
  CONFIRMATION_TOOLS,
  type MemorySuggestionAnswer,
  SILENT_TOOLS,
  asToolPart,
  bucketParts,
  getMemorySuggestionAnswers,
  getStaleGraphicArtifactToolCallIds,
  getSupersededContentCardToolCallIdsForMessages,
  getToolName,
  groupParts,
  isStreamingReasoning,
  isToolPartVisible,
  joinReasoning,
  shouldHideToolPart,
} from '@/features/assistant';
import { cn } from '@/lib/utils';

import { MemoryRecallBadge } from '@/features/assistant/_components/rich/memory-recall-badge';
import { ToolRenderer } from '@/features/assistant/_components/tool-renderer';
import { StreamErrorBanner } from './error-states';
import { ToolTrace } from './tool-trace';

interface MessageListProps {
  messages: UIMessage[];
  status: 'submitted' | 'streaming' | 'ready' | 'error';
  error: Error | null;
  onSendMessage: (
    text: string,
    selection?: ConfirmationSelection,
    footageSelection?: VideoFootageSelection
  ) => void;
  onRetry: () => void;
}

export function MessageList({
  messages,
  status,
  error,
  onSendMessage,
  onRetry,
}: MessageListProps) {
  const lastUserIndex = messages.reduce(
    (last, message, index) => (message.role === 'user' ? index : last),
    -1
  );
  const supersededContentCards =
    getSupersededContentCardToolCallIdsForMessages(messages);
  const staleGraphicArtifacts = getStaleGraphicArtifactToolCallIds(messages);
  const memorySuggestionAnswers = getMemorySuggestionAnswers(messages);
  const continuedCampaignErrors =
    getContinuedCampaignErrorToolCallIds(messages);

  return (
    <ApprovalCardStatusProvider messages={messages} status={status}>
      <Conversation className="min-h-0 flex-1">
        <CampaignPackageAdStatusRefresh messages={messages} />
        <ConversationContent className="mx-auto w-full max-w-3xl gap-4 px-4 py-4 sm:px-6 sm:py-6">
          {messages.map((message, index) => (
            <ComposerGateScope
              key={message.id}
              enabled={index > lastUserIndex}
              turn={messages[lastUserIndex]?.id ?? ''}
            >
              <MessageBubble
                message={message}
                continuedCampaignErrors={continuedCampaignErrors.get(
                  message.id
                )}
                supersededContentCards={supersededContentCards}
                memorySuggestionAnswers={memorySuggestionAnswers}
                staleGraphicArtifacts={staleGraphicArtifacts}
                isStreaming={
                  status === 'streaming' &&
                  message === messages[messages.length - 1]
                }
                onSendMessage={onSendMessage}
              />
            </ComposerGateScope>
          ))}

          {status === 'submitted' && <ThinkingIndicator />}

          {error && status === 'error' && (
            <StreamErrorBanner message={error.message} onRetry={onRetry} />
          )}
        </ConversationContent>
        <ConversationScrollButton aria-label="Scroll to bottom" />
      </Conversation>
    </ApprovalCardStatusProvider>
  );
}

interface MessageBubbleProps {
  message: UIMessage;
  continuedCampaignErrors?: ReadonlySet<string>;
  supersededContentCards: ReadonlySet<string>;
  staleGraphicArtifacts: ReadonlySet<string>;
  memorySuggestionAnswers: ReadonlyMap<string, MemorySuggestionAnswer>;
  isStreaming: boolean;
  onSendMessage: (
    text: string,
    selection?: ConfirmationSelection,
    footageSelection?: VideoFootageSelection
  ) => void;
}

function MessageBubble({
  message,
  continuedCampaignErrors,
  supersededContentCards,
  staleGraphicArtifacts,
  memorySuggestionAnswers,
  isStreaming,
  onSendMessage,
}: MessageBubbleProps) {
  const isUser = message.role === 'user';
  // Parts can change in place while streaming; recompute when the final card arrives.
  const packageSupersededAds = getPackageSupersededAdPreviewToolCallIds(
    message.parts
  );
  const preparationNotes = getPackagePreparationNotes(message);
  const recoveredCampaignErrors = getRecoveredCampaignErrorToolCallIds(
    message.parts
  );
  for (const id of continuedCampaignErrors ?? [])
    recoveredCampaignErrors.add(id);
  const sourceVideoId = isUser ? sourceVideoIdFromParts(message.parts) : null;
  // An answered graphic proposal retires only its own "hit Accept" line.
  const staleReply = useStaleProposalReply(message.parts);

  const {
    textParts,
    toolParts,
    reasoningParts,
    sourceParts,
    fileParts,
    memoryRecallParts,
  } = useMemo(() => bucketParts(message.parts), [message.parts]);

  // Q25a: auto-expand reasoning when a destructive confirmation is pending.
  const hasPendingConfirmation = toolParts.some(
    (tp) =>
      CONFIRMATION_TOOLS.has(getToolName(tp)) && tp.state !== 'output-available'
  );

  const hasReasoning = reasoningParts.length > 0;
  const reasoningStreaming =
    isStreaming &&
    hasReasoning &&
    reasoningParts.some((p) => isStreamingReasoning(p));

  const hasVisibleAssistantContent =
    textParts.length > 0 ||
    toolParts.some(
      (tp) =>
        !supersededContentCards.has(tp.toolCallId) &&
        isToolPartVisible(tp, message.role === 'assistant')
    ) ||
    hasReasoning ||
    sourceParts.length > 0 ||
    fileParts.length > 0 ||
    memoryRecallParts.length > 0;

  const renderPart = (part: UIMessage['parts'][number], i: number) => {
    if (part.type === 'text') {
      if (!isUser && hasVideoFootageRecoveryCard(message.parts)) return null;
      if (preparationNotes.partIndexes.has(i)) return null;
      if (isUser) {
        return (
          <p key={`${message.id}-text-${i}`} className="whitespace-pre-wrap">
            {cardReplyDisplay(part.text)}
          </p>
        );
      }
      const reply = staleReply.visibleReply(part.text);
      if (reply === null) return null;
      return (
        <MessageResponse
          key={`${message.id}-text-${i}`}
          // ChatGPT-style dot cursor while this text block streams in
          // (streamdown appends it after the last rendered block).
          caret="circle"
          isAnimating={isStreaming && i === message.parts.length - 1}
        >
          {reply}
        </MessageResponse>
      );
    }

    const tp = asToolPart(part);
    if (!tp) return null;
    if (
      recoveredCampaignErrors.has(tp.toolCallId) ||
      packageSupersededAds.has(tp.toolCallId) ||
      shouldHideToolPart(tp, { supersededContentCards, staleGraphicArtifacts })
    ) {
      return null;
    }

    const toolName = getToolName(tp);
    // A tool can be in SILENT_TOOLS (no chain-of-thought trace badge) and
    // *still* produce a rich output that should render — e.g.
    // `createDraftVideo` returns `uiState: 'created'` and is meant to mount
    // the CreatedCard via the generic dispatch in ToolRenderer. Only silence
    // the part when its output has no interactive shape (no `uiState`).
    // A silent tool still shows a CARD: its trace badge is what SILENT_TOOLS
    // suppresses, not its output. Keying only on `uiState` hid every
    // `confirmation_required` a silent tool raised (a budget change showed
    // "approval card is waiting" over nothing).
    const out = tp.output as
      | { uiState?: unknown; presentation?: { type?: unknown } }
      | undefined;
    const hasInteractiveOutput =
      !!out &&
      typeof out === 'object' &&
      ('uiState' in out ||
        (typeof out.presentation?.type === 'string' &&
          out.presentation.type !== 'none'));
    if (
      SILENT_TOOLS.has(toolName) &&
      tp.state === 'output-available' &&
      !hasInteractiveOutput
    ) {
      return null;
    }

    return (
      <ToolRenderer
        key={`${tp.toolCallId}-${i}`}
        toolName={toolName}
        toolPart={tp}
        onSendMessage={onSendMessage}
        suppressArtifactHighlight={staleGraphicArtifacts.has(tp.toolCallId)}
        memorySuggestionAnswer={memorySuggestionAnswers.get(tp.toolCallId)}
        onReplyStale={staleReply.markReplyStale}
        isStreaming={isStreaming}
      />
    );
  };

  return (
    <Message from={message.role}>
      <MessageContent
        className={cn(
          message.role === 'assistant' && 'min-w-0 max-w-[95%] gap-3'
        )}
      >
        {/* Image attachments (W-C11) — render before text/tool output so the
            visual order matches what Anthropic vision sees (image content
            blocks precede text in `convert-to-anthropic-messages`). */}
        {sourceVideoId && <SourceVideoPreview videoId={sourceVideoId} />}
        {!sourceVideoId && fileParts.length > 0 && (
          <Attachments className="mb-1" variant="grid">
            {fileParts.map((fp, i) => (
              <Attachment
                key={`${message.id}-file-${i}`}
                data={{
                  type: 'file',
                  id: `${message.id}-file-${i}`,
                  mediaType: fp.mediaType,
                  url: fp.url,
                  filename: fp.filename,
                }}
              >
                <AttachmentPreview />
              </Attachment>
            ))}
          </Attachments>
        )}

        {/* Reasoning — collapsed by default; auto-expand on destructive. */}
        {!isUser && hasReasoning && (
          <Reasoning
            isStreaming={reasoningStreaming}
            defaultOpen={hasPendingConfirmation}
          >
            <ReasoningTrigger />
            <ReasoningContent>{joinReasoning(reasoningParts)}</ReasoningContent>
          </Reasoning>
        )}

        {/* Tool trace — only when there are tool calls. */}
        {!isUser && toolParts.length > 0 && (
          <ToolTrace
            parts={message.parts}
            isStreaming={isStreaming}
            preparationNotes={preparationNotes.texts}
          />
        )}

        {/* Text + rich tool parts in original order. Consecutive draft-ad
            cards (the campaign builds a video ad + a graphic ad back-to-back)
            are laid out side by side in a row instead of stacked. */}
        {groupParts(message.parts).map((unit) =>
          unit.kind === 'ad-row' ? (
            <div
              key={`adrow-${unit.items[0].index}`}
              className="flex flex-col gap-3 sm:flex-row sm:items-start"
            >
              {unit.items.map(({ part, index }) => (
                <div
                  key={`${message.id}-adcell-${index}`}
                  className="min-w-0 flex-1"
                >
                  {renderPart(part, index)}
                </div>
              ))}
            </div>
          ) : (
            renderPart(unit.part, unit.index)
          )
        )}

        {/* Memory-recall badges — render below text, before sources, so the
            "Claire remembered…" chip sits as a footer on the message body
            without dominating it. Gated on backend emit (no-op today). */}
        {!isUser && memoryRecallParts.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {memoryRecallParts.map((payload) => (
              <MemoryRecallBadge
                key={payload.knowledgeEntryId}
                payload={payload}
              />
            ))}
          </div>
        )}

        {/* Sources — render below the message body. */}
        {!isUser && sourceParts.length > 0 && (
          <Sources>
            <SourcesTrigger count={sourceParts.length} />
            <SourcesContent>
              {sourceParts.map((src, i) => (
                <Source
                  key={`${message.id}-src-${i}`}
                  href={src.url}
                  title={src.title ?? src.url}
                />
              ))}
            </SourcesContent>
          </Sources>
        )}

        {/* Streaming turns open with non-visible parts (e.g. `step-start`)
            before the first text delta lands — show the typing dot for that
            beat instead of flashing "No content". */}
        {!isUser && !hasVisibleAssistantContent && isStreaming && <TypingDot />}
      </MessageContent>
    </Message>
  );
}

/** ChatGPT-style pulsing dot — shown while Claire streams with nothing visible yet. */
function TypingDot() {
  return (
    <span
      aria-label="Claire is typing"
      className="inline-block animate-pulse select-none leading-none"
    >
      ●
    </span>
  );
}

function ThinkingIndicator() {
  return (
    <Message from="assistant">
      <MessageContent>
        <div className="flex items-center gap-2 rounded-lg bg-muted px-4 py-2.5 text-sm text-muted-foreground">
          <Bot className="size-4" />
          <div className="flex gap-1">
            <span className="size-1.5 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:0ms]" />
            <span className="size-1.5 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:150ms]" />
            <span className="size-1.5 animate-bounce rounded-full bg-muted-foreground/60 [animation-delay:300ms]" />
          </div>
          <span>Thinking...</span>
        </div>
      </MessageContent>
    </Message>
  );
}
